/**
 * Theme Preview Page - Accessible via temporary preview token
 * No authentication required for viewing
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemePreviewContent } from './ThemePreviewContent';

interface PreviewThemeData {
  version: string;
  theme: {
    colors: Record<string, string>;
    typography: Record<string, string | number>;
    spacing: Record<string, string>;
    brand: Record<string, string>;
    shadows: Record<string, string>;
    layout: Record<string, string>;
    metadata: {
      version: string;
      deviceType: 'desktop' | 'mobile';
      updatedAt: string;
    };
  };
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

export function ThemePreviewPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<PreviewThemeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceType, setDeviceType] = useState<'desktop' | 'mobile'>('desktop');

  useEffect(() => {
    if (!token) {
      setError('無效的預覽連結');
      setLoading(false);
      return;
    }

    async function loadPreviewTheme() {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/api/themes/preview/${token}`);

        if (!response.ok) {
          if (response.status === 404) {
            setError('預覽連結無效或已過期');
          } else {
            setError('載入主題預覽失敗');
          }
          setLoading(false);
          return;
        }

        const result: ApiResponse<PreviewThemeData> = await response.json();

        if (result.success && result.data) {
          setData(result.data);
          // Set device type based on theme metadata
          setDeviceType(result.data.theme.metadata.deviceType);
        } else {
          setError(result.error?.message || '載入主題預覽失敗');
        }
      } catch (err) {
        console.error('Error loading preview theme:', err);
        setError('網路錯誤，請稍後再試');
      } finally {
        setLoading(false);
      }
    }

    loadPreviewTheme();
  }, [token]);

  if (loading) {
    return (
      <div className="preview-loading">
        <div className="spinner" />
        <p>載入主題預覽中...</p>
        <style>{`
          .preview-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
          .spinner {
            width: 40px;
            height: 40px;
            border: 3px solid #f3f3f3;
            border-top: 3px solid #d4af37;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          p {
            margin-top: 1rem;
            color: #666;
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="preview-error">
        <div className="error-icon">⚠️</div>
        <h2>無法載入預覽</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')}>返回首頁</button>
        <style>{`
          .preview-error {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            padding: 2rem;
            text-align: center;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }
          .error-icon {
            font-size: 3rem;
            margin-bottom: 1rem;
          }
          h2 {
            margin: 0 0 0.5rem 0;
            color: #333;
          }
          p {
            margin: 0 0 1.5rem 0;
            color: #666;
          }
          button {
            padding: 0.75rem 1.5rem;
            background: #d4af37;
            color: white;
            border: none;
            border-radius: 8px;
            cursor: pointer;
            font-size: 1rem;
          }
          button:hover {
            background: #c9a030;
          }
        `}</style>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <div className="theme-preview-page">
      <header className="preview-header">
        <div className="preview-header-content">
          <div className="preview-info">
            <h1>主題預覽</h1>
            <span className="version-badge">版本: {data.version}</span>
          </div>
          <div className="preview-controls">
            <button
              className={`device-toggle ${deviceType === 'desktop' ? 'active' : ''}`}
              onClick={() => setDeviceType('desktop')}
            >
              桌面版
            </button>
            <button
              className={`device-toggle ${deviceType === 'mobile' ? 'active' : ''}`}
              onClick={() => setDeviceType('mobile')}
            >
              手機版
            </button>
          </div>
        </div>
      </header>

      <main className="preview-content">
        <ThemePreviewContent
          theme={data.theme}
          deviceType={deviceType}
        />
      </main>

      <style>{`
        .theme-preview-page {
          min-height: 100vh;
          background: #f5f5f5;
        }
        .preview-header {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .preview-header-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 1rem 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .preview-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .preview-info h1 {
          margin: 0;
          font-size: 1.25rem;
          color: #333;
        }
        .version-badge {
          padding: 0.25rem 0.75rem;
          background: #f0f0f0;
          border-radius: 20px;
          font-size: 0.875rem;
          color: #666;
        }
        .preview-controls {
          display: flex;
          gap: 0.5rem;
        }
        .device-toggle {
          padding: 0.5rem 1rem;
          border: 1px solid #e0e0e0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.2s;
        }
        .device-toggle:hover {
          background: #f5f5f5;
        }
        .device-toggle.active {
          background: #d4af37;
          color: white;
          border-color: #d4af37;
        }
        .preview-content {
          max-width: 1400px;
          margin: 0 auto;
          padding: 2rem 1.5rem;
        }
        @media (max-width: 768px) {
          .preview-header-content {
            flex-direction: column;
            gap: 1rem;
          }
        }
      `}</style>
    </div>
  );
}

export default ThemePreviewPage;
